import { lazy, Suspense, useEffect, useState } from 'react';
import './App.css';
import './ProductTheme.generated.css';
import './ProductTheme.css';
import { supabase } from './supabase';
import Landing from './screens/Landing';
import RecoverAuth from './screens/RecoverAuth';
import RecoverDashboard from './screens/RecoverDashboard';
import RecoverBookingPublic from './screens/RecoverBookingPublic';
import RecoverLegal from './screens/RecoverLegal';
import PublicBooking from './screens/PublicBooking';
import ManageBooking from './screens/ManageBooking';
import FounderPortal from './screens/FounderPortal';
import PublicMenu from './screens/PublicMenu';

const LandingAuthkit = lazy(() => import('./screens/LandingAuthkit'));
const LandingStripe = lazy(() => import('./screens/LandingStripe'));
const LandingDiscord = lazy(() => import('./screens/LandingDiscord'));
const MenuLanding = lazy(() => import('./screens/MenuLanding'));

function currentRoute() {
  return `${window.location.pathname}${window.location.search}`;
}

export function navigate(path) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new Event('ristodaily:navigate'));
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function FounderRedirect() {
  useEffect(() => {
    window.location.replace('https://founder.ristodaily.com');
  }, []);
  return <div className="app-loader"><span /></div>;
}

function AppRouter() {
  const [route, setRoute] = useState(currentRoute);
  const [session, setSession] = useState(undefined);

  useEffect(() => {
    const onNavigate = () => setRoute(currentRoute());
    window.addEventListener('popstate', onNavigate);
    window.addEventListener('ristodaily:navigate', onNavigate);
    return () => {
      window.removeEventListener('popstate', onNavigate);
      window.removeEventListener('ristodaily:navigate', onNavigate);
    };
  }, []);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setSession(data.session || null);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (active) setSession(nextSession || null);
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const pathname = route.split('?')[0].replace(/\/+$/, '') || '/';
  const founderHost = window.location.hostname.toLowerCase() === 'founder.ristodaily.com';
  const founderRoute = founderHost || pathname === '/founder';
  const bookingMatch = pathname.match(/^\/recover\/prenota\/([^/]+)$/);
  const directBookingMatch = pathname.match(/^\/prenota\/([^/]+)$/);
  const manageBookingMatch = pathname.match(/^\/(?:prenotazione|p)\/([^/]+)$/);
  const menuMatch = pathname.match(/^\/menu\/([^/]+)$/);

  if (founderRoute) {
    if (session === undefined) return <div className="app-loader"><span /></div>;
    return <FounderPortal session={session} />;
  }
  if (bookingMatch) {
    return <RecoverBookingPublic token={decodeURIComponent(bookingMatch[1])} />;
  }
  if (menuMatch) return <PublicMenu slug={decodeURIComponent(menuMatch[1])} />;
  if (pathname === '/menu-digitale') return <Suspense fallback={<div role="status">Caricamento menù digitale…</div>}><MenuLanding /></Suspense>;
  if (pathname === '/prova-authkit') {
    return <Suspense fallback={<div role="status">Caricamento anteprima…</div>}><LandingAuthkit /></Suspense>;
  }
  if (pathname === '/prova-stripe') {
    return <Suspense fallback={<div role="status">Caricamento anteprima…</div>}><LandingStripe /></Suspense>;
  }
  if (pathname === '/prova-discord') {
    return <Suspense fallback={<div role="status">Caricamento anteprima…</div>}><LandingDiscord /></Suspense>;
  }
  if (directBookingMatch) {
    return <PublicBooking slug={decodeURIComponent(directBookingMatch[1])} />;
  }
  if (manageBookingMatch) {
    return <ManageBooking token={decodeURIComponent(manageBookingMatch[1])} />;
  }
  if (pathname === '/privacy' || pathname === '/termini' || pathname === '/cookie') {
    return <RecoverLegal type={pathname.slice(1)} onBack={() => navigate('/')} />;
  }
  if (pathname === '/app') {
    if (session === undefined) return <div className="app-loader"><span /></div>;
    const query = new URLSearchParams(window.location.search);
    if (query.has('reset')) return <RecoverAuth resetMode session={session} />;
    // Entry context affects presentation only, never permissions or tenant access.
    const menuEntry = query.get('from') === 'menu' || query.get('view') === 'menu';
    return session
      ? <RecoverDashboard session={session} initialView={menuEntry || session.user.user_metadata?.entry_point === 'menu' ? 'menu' : 'bookings'} onLogout={() => navigate('/')} />
      : <RecoverAuth initialSignup={query.has('signup')} entryPoint={menuEntry ? 'menu' : ''} />;
  }
  if (pathname === '/admin') {
    return <FounderRedirect />;
  }
  return <Landing />;
}

function App() {
  return <div className="rd-product"><AppRouter /></div>;
}

export default App;
